import React, { useState, useEffect } from 'react';
import { Typography, Grid, Paper, Button, Divider, FormControl, InputLabel, Select, MenuItem, Box, Card } from '@mui/material';
import { useNavigate, useParams } from 'react-router';
import { getApi } from 'views/services/api';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import LocalHotelIcon from '@mui/icons-material/LocalHotel';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { height, width } from '@mui/system';

const SingleInvoiceView = () => {
  const [invoiceData, setInvoiceData] = useState({});
  const [currencyCode, setCurrencyCode] = useState('');
  const [itemsData, setitemsData] = useState([]);
  const [printSize, setPrintSize] = useState('A5');
  const hotel = JSON.parse(localStorage.getItem('hotelData'));
  const params = useParams();
  const reservationId = params.id;
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSingleBillData = async () => {
      try {
        const response = await getApi(`api/singleinvoice/view/${reservationId}`);
        setInvoiceData(response?.data?.InvoiceData[0]);
        if (hotel?.currencyCode === 'INR') {
          setCurrencyCode('\u20B9');
        } else {
          setCurrencyCode(hotel?.currencyCode);
        }
      } catch (error) {
        console.log(error);
      }
    };
    fetchSingleBillData();
  }, [reservationId]);

  useEffect(() => {
    const fetchitemsData = async () => {
      try {
        const response = await getApi(`api/reservation/getfooditems/${reservationId}`);
        setitemsData(response?.data?.foodItemsData[0]?.foodItems || []);
      } catch (error) {
        console.log(error);
      }
    };
    fetchitemsData();
  }, [reservationId]);

  const handlePrint = () => {
    window.print();
  };

  const handlePrintSizeChange = (event) => {
    setPrintSize(event.target.value);
  };

  const currentDate = new Date(invoiceData?.createdDate).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });

  return (
    <Box sx={{ padding: '20px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header with controls */}
      <Box className="no-print" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          sx={{
            color: '#5a4b81',
            borderColor: '#5a4b81',
            '&:hover': {
              backgroundColor: '#f0edf5',
              borderColor: '#5a4b81'
            }
          }}
          onClick={() => navigate(-1)}
        >
          Back
        </Button>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl sx={{ minWidth: 120 }} size="small">
            <InputLabel id="print-size-label">Paper Size</InputLabel>
            <Select
              labelId="print-size-label"
              value={printSize}
              label="Paper Size"
              onChange={handlePrintSizeChange}
            >
              <MenuItem value="A4">A4</MenuItem>
              <MenuItem value="A5">A5</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            onClick={handlePrint}
            startIcon={<PrintIcon />}
            sx={{
              backgroundColor: '#5a4b81',
              '&:hover': {
                backgroundColor: '#42385e'
              }
            }}
          >
            Print Invoice
          </Button>
        </Box>
      </Box>

      {/* Invoice Content */}
      <Box className="invoice-container" sx={{
        backgroundColor: 'white',
        padding: '30px',
        maxWidth: printSize === 'A4' ? '800px' : '600px',
        margin: '0 auto',
        boxShadow: '0px 5px 15px rgba(0,0,0,0.1)',
        border: '1px solid #e0e0e0',
        borderRadius: '8px'
      }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1,gap:1 }}>
           <img src={`https://neorooms.in/api/${hotel?.hotelImage}`}  style={{ width:"40px",height:"40px" }} />
              <Typography variant="h4" sx={{ color: '#5a4b81', fontWeight: 'bold' }}>
                {hotel?.name}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: '#666' }}>{hotel?.address}</Typography>
            <Typography variant="body2" sx={{ color: '#666' }}>Tel: {hotel?.contact}</Typography>
            {invoiceData?.gstNumber && (
              <Typography variant="body2" sx={{ color: '#666' }}>GST: {invoiceData?.gstNumber}</Typography>
            )}
          </Box>

          <Box sx={{ textAlign: 'right' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 1 }}>
              <ReceiptIcon sx={{ color: '#5a4b81', mr: 1 }} />
              <Typography variant="h5" sx={{ color: '#5a4b81', fontWeight: 'bold' }}>INVOICE</Typography>
            </Box>
            <Typography variant="body2"><strong>Invoice #:</strong> {invoiceData?.invoiceNumber}</Typography>
            <Typography variant="body2"><strong>Date:</strong> {currentDate}</Typography>
            <Typography variant="body2"><strong>Type:</strong> Food + Room</Typography>
          </Box>
        </Box>

        <Divider sx={{ borderColor: '#5a4b81', borderWidth: '1px', mb: 2 }} />

        <Grid container spacing={3}>
          {/* Customer Details */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" sx={{ color: '#5a4b81', fontWeight: 'bold', mb: 1 }}>BILL TO</Typography>
            <Card variant="outlined" sx={{ padding: '16px', backgroundColor: '#f9f9f9' }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{invoiceData?.name}</Typography>
              <Typography variant="body2">{invoiceData?.address}</Typography>
              <Typography variant="body2">Phone: {invoiceData?.customerPhoneNumber}</Typography>
            </Card>
          </Grid>

          {/* Stay Details */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" sx={{ color: '#5a4b81', fontWeight: 'bold', mb: 1 }}>STAY DETAILS</Typography>
            <Card variant="outlined" sx={{ padding: '16px', backgroundColor: '#f9f9f9' }}>
              {invoiceData?.FinalCheckInTime && <Typography variant="body2"><strong>Check-In:</strong> {invoiceData?.FinalCheckInTime}</Typography>}
              {invoiceData?.FinalCheckOutTime && <Typography variant="body2"><strong>Check-Out:</strong> {invoiceData?.FinalCheckOutTime}</Typography>}
              {invoiceData?.advancePaymentMethod && <Typography variant="body2"><strong>Advance Payment:</strong> {invoiceData?.advancePaymentMethod}</Typography>}
              {invoiceData?.paymentOption && <Typography variant="body2"><strong>Payment Method:</strong> {invoiceData?.paymentOption}</Typography>}
            </Card>
          </Grid>

          {/* Unified Invoice Table */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="h6" sx={{ color: '#5a4b81', fontWeight: 'bold', mb: 2 }}>INVOICE DETAILS</Typography>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f0edf5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Price</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Qty</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Room Details */}
                  {invoiceData?.roomRent && (
                    <TableRow>
                      <TableCell><b>Room Charges</b></TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell align="right"><b>{currencyCode} {invoiceData?.pendingAmount + invoiceData?.advanceAmount - invoiceData?.extraBedsCharge}</b></TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell><b>Extra Bed Charges</b></TableCell>
                    <TableCell>-</TableCell>
                    <TableCell>-</TableCell>
                    <TableCell align="right"><b>{currencyCode} {invoiceData?.extraBedsCharge || 0}</b></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><b>Extra Stay Charge</b></TableCell>
                    <TableCell>-</TableCell>
                    <TableCell>-</TableCell>
                    <TableCell align="right"><b>{currencyCode} {invoiceData?.extraStayCharge || 0}</b></TableCell>
                  </TableRow>

                  {/* Food Details (if exist) */}
                  {itemsData?.length > 0 && itemsData.map((foodItem) => (
                    <TableRow key={foodItem._id}>
                      <TableCell>{foodItem.name}</TableCell>
                      <TableCell>{currencyCode} {foodItem.price}</TableCell>
                      <TableCell>{foodItem.quantity}</TableCell>
                      <TableCell align="right">{currencyCode} {foodItem.totalAmountFood}</TableCell>
                    </TableRow>
                  ))}

                  {/* Food totals if exist */}
                  {invoiceData?.foodAmount > 0 && (
                    <TableRow>
                      <TableCell><b>Food Amount</b></TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell align="right"><b>{currencyCode} {invoiceData.foodAmount}</b></TableCell>
                    </TableRow>
                  )}

                  {/* Discounts & GST */}
                  <TableRow>
                    <TableCell><b>Discount</b></TableCell>
                    <TableCell>-</TableCell>
                    <TableCell>-</TableCell>
                    <TableCell align="right" sx={{ color: 'red' }}><b>- {currencyCode} {(invoiceData?.roomDiscount || 0) + (invoiceData?.foodDiscount || 0)}</b></TableCell>
                  </TableRow>

                  {invoiceData?.haveRoomGst && (
                    <TableRow>
                      <TableCell><b>Room GST</b></TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell align="right"><b>{currencyCode} {invoiceData?.roomGstAmount}</b></TableCell>
                    </TableRow>
                  )}
                  {invoiceData?.haveFoodGst && (
                    <TableRow>
                      <TableCell><b>Food GST</b></TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell align="right"><b>{currencyCode} {invoiceData?.foodGstAmount}</b></TableCell>
                    </TableRow>
                  )}

                  {/* Final Totals */}
                  <TableRow sx={{ borderTop: '2px solid #ddd' }}>
                    <TableCell><b>Grand Total</b></TableCell>
                    <TableCell>-</TableCell>
                    <TableCell>-</TableCell>
                    <TableCell align="right"><b>{currencyCode} {invoiceData?.totalFoodAndRoomAmount}</b></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><b>Advance Paid</b></TableCell>
                    <TableCell>-</TableCell>
                    <TableCell>-</TableCell>
                    <TableCell align="right"><b>{currencyCode} {invoiceData?.advanceAmount || 0}</b></TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#fff5f5' }}>
                    <TableCell><b>Balance Due</b></TableCell>
                    <TableCell>-</TableCell>
                    <TableCell>-</TableCell>
                    <TableCell align="right"><b>{currencyCode} {invoiceData?.pendingAmount + invoiceData?.foodAmount}</b></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {/* Footer */}
          <Grid item xs={12}>
            <Box sx={{ mt: 1, p: 1, backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>Thank you for your stay. We look forward to serving you again!</Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mt: 4 }}>
            <Box sx={{ textAlign: 'center', flex: 1 }}>
              <Typography variant="body2" sx={{ borderTop: '1px solid #000', display: 'inline-block', paddingTop: '10px', minWidth: '150px' }}>
                Guest Signature
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center', flex: 1 }}>
              <Typography variant="body2" sx={{ borderTop: '1px solid #000', display: 'inline-block', paddingTop: '10px', minWidth: '150px' }}>
                Authorized Signature
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Print Styles */}
      <style>
        {`
          @media print {
            @page { size: ${printSize}; margin: 0; }
              body, html {
    margin: 0 !important;
    padding: 0 !important;
    background: white;
  }
            .no-print { display: none !important; }
            .invoice-container { box-shadow: none !important; padding: 0 !important; max-width: 100% !important; margin: 0 !important; border: none !important; }
            table { font-size: ${printSize === 'A5' ? '9px' : '11px'}; }
            .MuiTypography-h4 { font-size: ${printSize === 'A5' ? '1.3rem' : '1.55rem'} !important; }
            .MuiTypography-h5 { font-size: ${printSize === 'A5' ? '1.05rem' : '1.3rem'} !important; }
            .MuiTypography-h6 { font-size: ${printSize === 'A5' ? '0.8rem' : '1.05rem'} !important; }
            .MuiTypography-body2 { font-size: ${printSize === 'A5' ? '0.55rem' : '0.675rem'} !important; }
          }
        `}
      </style>
    </Box>
  );
};

export default SingleInvoiceView;
